import { Card, Checkbox, useToast } from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';

import {
  fetchNotificationPreferences,
  updateNotificationPreferences,
} from '../../notifications/api';

/**
 * `notification-preferences/spec.md`: own preferences, self-scoped. No
 * equivalent section exists yet in `SecurityPage` (TOTP only), so this is
 * a new sibling route under `/account`.
 */
export function NotificationPreferencesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const preferences = useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: fetchNotificationPreferences,
  });

  const update = useMutation({
    mutationFn: updateNotificationPreferences,
    onSuccess: (data) => {
      queryClient.setQueryData(['notifications', 'preferences'], data);
      toast('success', 'Preferencias actualizadas');
    },
    onError: () => {
      toast('error', 'No se pudieron actualizar las preferencias');
    },
  });

  return (
    <section className="max-w-md">
      <h1 className="text-xl font-semibold text-slate-900">Notificaciones</h1>
      <p className="mt-1 text-sm text-slate-500">Elegí qué avisos no críticos querés recibir</p>

      <div className="mt-6">
        {preferences.data === undefined ? (
          <p className="text-sm text-slate-500">Cargando...</p>
        ) : (
          <Card className="flex flex-col gap-4">
            <Checkbox
              label="Avisos no críticos por email"
              checked={preferences.data.emailNonCriticalEnabled}
              onChange={(event) =>
                update.mutate({
                  emailNonCriticalEnabled: event.target.checked,
                  inAppNonCriticalEnabled: preferences.data?.inAppNonCriticalEnabled,
                })
              }
              disabled={update.isPending}
            />
            <Checkbox
              label="Avisos no críticos en la campana"
              checked={preferences.data.inAppNonCriticalEnabled}
              onChange={(event) =>
                update.mutate({
                  emailNonCriticalEnabled: preferences.data?.emailNonCriticalEnabled,
                  inAppNonCriticalEnabled: event.target.checked,
                })
              }
              disabled={update.isPending}
            />
            <p className="text-xs text-slate-400">
              Las confirmaciones directas de tus pedidos siempre se envían, incluso con estos avisos
              desactivados.
            </p>
          </Card>
        )}
      </div>
    </section>
  );
}
